<template>
    <div class="space-y-xs">
        <v-page-product-configurator-attribute v-for="(configurableAttribute, index) in configuration.attributes"
            :key="configurableAttribute.attribute.id"
            v-model="selections"
            :index="index"
            :configurable-attribute="configurableAttribute"
            @update:model-value="clearSelections(index)" />

        <v-page-product-configurator-property v-for="property in productProperties"
            :key="property.id"
            v-model="propertyValues[property.id]"
            :product-property="property" />
    </div>
</template>

<script setup lang="ts">
import { useVModel, whenever } from '@vueuse/core';
import { intersection } from 'es-toolkit';
import type { SimpleProductDataFragment, ViewProductQuery } from '~~/graphql';
import type { ProductKind } from '~~/types';

type Configuration = Extract<ViewProductQuery['product'], { __typename: 'ConfigurableProduct' }>['configuration'];
type ProductProperty = ProductKind<ViewProductQuery['product']>['properties'][0];
type ConfigurableAttributeOption = Configuration['attributes'][0]['options'][0];
type ConfigurableProduct = Configuration['products'][0];

const properties = defineProps<{
    selectedProduct?: ConfigurableProduct
    selectedProperties: Record<string, string>
    configurableProduct?: ConfigurableProduct
    configuration: Configuration
    productProperties: ProductProperty[]
}>();

const emit = defineEmits<{
    (event: 'update:selectedProduct', value: SimpleProductDataFragment | undefined): void
    (event: 'update:selectedProperties', value: Record<string, string>): void
}>();

const selections = ref<(ConfigurableAttributeOption | undefined)[]>([]);
const propertyValues = useVModel(properties, 'selectedProperties', emit);

whenever(() => properties.selectedProduct, (value) => {
    selections.value = properties.configuration.attributes
        .map((configurableAttribute) => (
            configurableAttribute.options
                .find((option) => option.products.some((item) => item.id === value?.id))
        ));
}, { immediate: true });

const productIds = computed(() => {
    const selectedProductIds = selections.value
        .map((option) => option?.products.map((product) => product.id) || []);

    let result: string[] = [];

    for (const [index, ids] of selectedProductIds.entries()) {
        result = index === 0 ? ids : intersection(result, ids);
    }

    return result;
});

watch(productIds, (value) => {
    if (value.length === 1) {
        const product = properties.configuration.products.find((item) => item.id === value[0]);

        emit('update:selectedProduct', product);
    } else {
        emit('update:selectedProduct', undefined);
    }
});

function clearSelections(index: number) {
    selections.value = selections.value.slice(0, index + 1);
}
</script>
